import React, { useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Plus, Trash2, Loader2, GripVertical, Check, X } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const CORES = [
	"#3b82f6",
	"#eab308",
	"#a855f7",
	"#22c55e",
	"#ef4444",
	"#f97316",
	"#06b6d4",
	"#64748b",
];

/**
 * Gerencia as colunas do quadro de leads: nome, cor, ordem, criar e excluir.
 *
 * O nome é livre; o identificador interno de cada etapa nunca muda, então
 * renomear "Novo" para "Primeiro contato" não move nenhum lead de lugar nem
 * quebra as mensagens automáticas configuradas por etapa.
 */
const LeadStagesModal = ({ open, onClose, onChanged }) => {
	const [etapas, setEtapas] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [novoNome, setNovoNome] = useState("");
	const [novaCor, setNovaCor] = useState(CORES[0]);
	const [salvando, setSalvando] = useState(false);

	// Etapa que está sendo apagada e para onde os leads dela vão.
	const [removendo, setRemovendo] = useState(null);
	const [destino, setDestino] = useState("");

	const carregar = async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/crm/leads/stages");
			setEtapas(Array.isArray(data) ? data : []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	};

	useEffect(() => {
		if (open) carregar();
	}, [open]);

	const avisar = () => {
		carregar();
		onChanged?.();
	};

	const criar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			await api.post("/crm/leads/stages", { name: novoNome, color: novaCor });
			toast.success("Etapa criada.");
			setNovoNome("");
			avisar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarEtapa = async (etapa, mudancas) => {
		try {
			await api.put(`/crm/leads/stages/${etapa.id}`, mudancas);
			avisar();
		} catch (err) {
			toastError(err);
		}
	};

	const mover = async (indice, direcao) => {
		const nova = [...etapas];
		const alvo = indice + direcao;

		if (alvo < 0 || alvo >= nova.length) return;

		[nova[indice], nova[alvo]] = [nova[alvo], nova[indice]];
		setEtapas(nova);

		try {
			await api.put("/crm/leads/stages/reorder", {
				ids: nova.map((e) => e.id),
			});
			onChanged?.();
		} catch (err) {
			toastError(err);
			carregar();
		}
	};

	const excluir = async () => {
		try {
			await api.delete(`/crm/leads/stages/${removendo.id}`, {
				data: destino ? { moverPara: destino } : {},
			});
			toast.success("Etapa removida.");
			setRemovendo(null);
			setDestino("");
			avisar();
		} catch (err) {
			// O backend recusa apagar etapa com lead sem destino e explica o
			// porquê — mostrar a mensagem dele é mais útil que um erro genérico.
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Etapas do quadro</DialogTitle>
					<DialogDescription>
						As colunas do quadro de leads. Renomear não move ninguém de lugar.
					</DialogDescription>
				</DialogHeader>

				{carregando ? (
					<div className="py-8 text-center">
						<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : removendo ? (
					<div className="space-y-3 py-2">
						<p className="text-sm leading-relaxed">
							Excluir a etapa{" "}
							<span className="font-medium">{removendo.name}</span>. Se houver
							leads nela, escolha para onde vão — sem isso eles sumiriam do
							quadro.
						</p>
						<div>
							<Label htmlFor="destino">Mover os leads para</Label>
							<Select value={destino} onValueChange={setDestino}>
								<SelectTrigger id="destino">
									<SelectValue placeholder="Escolha a etapa" />
								</SelectTrigger>
								<SelectContent>
									{etapas
										.filter((e) => e.id !== removendo.id)
										.map((e) => (
											<SelectItem key={e.id} value={e.slug}>
												{e.name}
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex justify-end gap-2 pt-1">
							<Button
								variant="ghost"
								onClick={() => {
									setRemovendo(null);
									setDestino("");
								}}
							>
								Cancelar
							</Button>
							<Button variant="destructive" onClick={excluir}>
								Excluir etapa
							</Button>
						</div>
					</div>
				) : (
					<>
						<div className="max-h-[45vh] space-y-2 overflow-y-auto py-1">
							{etapas.map((etapa, indice) => (
								<div
									key={etapa.id}
									className="flex items-center gap-2 rounded-lg border p-2"
								>
									<div className="flex flex-col">
										<button
											type="button"
											aria-label={`Subir ${etapa.name}`}
											disabled={indice === 0}
											onClick={() => mover(indice, -1)}
											className="text-xs text-muted-foreground disabled:opacity-30"
										>
											▲
										</button>
										<button
											type="button"
											aria-label={`Descer ${etapa.name}`}
											disabled={indice === etapas.length - 1}
											onClick={() => mover(indice, 1)}
											className="text-xs text-muted-foreground disabled:opacity-30"
										>
											▼
										</button>
									</div>

									<input
										type="color"
										value={etapa.color}
										onChange={(e) =>
											salvarEtapa(etapa, { color: e.target.value })
										}
										aria-label={`Cor de ${etapa.name}`}
										className="h-7 w-7 shrink-0 cursor-pointer rounded border bg-transparent"
									/>

									<Input
										defaultValue={etapa.name}
										aria-label={`Nome de ${etapa.name}`}
										className="h-8 flex-1"
										onBlur={(e) => {
											if (e.target.value.trim() && e.target.value !== etapa.name) {
												salvarEtapa(etapa, { name: e.target.value });
											}
										}}
									/>

									<Button
										variant="ghost"
										size="icon"
										className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
										aria-label={`Excluir ${etapa.name}`}
										onClick={() => setRemovendo(etapa)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								</div>
							))}
						</div>

						<form onSubmit={criar} className="border-t pt-3">
							<Label htmlFor="novaEtapa">Nova etapa</Label>
							<div className="mt-1 flex gap-2">
								<input
									type="color"
									value={novaCor}
									onChange={(e) => setNovaCor(e.target.value)}
									aria-label="Cor da nova etapa"
									className="h-9 w-9 shrink-0 cursor-pointer rounded border bg-transparent"
								/>
								<Input
									id="novaEtapa"
									placeholder="Ex.: Visita agendada"
									value={novoNome}
									onChange={(e) => setNovoNome(e.target.value)}
								/>
								<Button type="submit" disabled={salvando || !novoNome.trim()}>
									{salvando ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Plus className="h-4 w-4" />
									)}
									Criar
								</Button>
							</div>
						</form>
					</>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Fechar
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default LeadStagesModal;
